import React from "react";
import { Link } from "react-router-dom";
import { Phone, ArrowRight } from "lucide-react";
import { SITE, IMAGES } from "@/lib/site";
import PriceBanner from "@/components/PriceBanner";
import SectionTitle from "@/components/SectionTitle";
import usePageMeta from "@/hooks/usePageMeta";

const STEPS = [
  { n: "01", title: "Premier appel — à toute heure", desc: "Nous vous écoutons, vous rassurons et lançons les premières formalités urgentes (constat, transport, déclaration en mairie)." },
  { n: "02", title: "Rendez-vous d'organisation", desc: "À domicile, en agence ou par téléphone : nous définissons ensemble la cérémonie, les volontés du défunt et le budget." },
  { n: "03", title: "Coordination des intervenants", desc: "Officiant, mairie, cimetière ou crématorium, fleuriste, marbrier, presse : nous coordonnons tous les acteurs." },
  { n: "04", title: "Cérémonie & hommage", desc: "Civile ou religieuse, intime ou solennelle. Nous orchestrons chaque détail pour que vous puissiez vous recueillir." },
  { n: "05", title: "Démarches après obsèques", desc: "Capital décès, sécurité sociale, mutuelle, banque : nous restons à vos côtés pour les démarches qui suivent." },
];

const TYPES = [
  { title: "Cérémonie civile", desc: "Un hommage personnel, laïc, structuré autour de la mémoire et des proches : musiques, lectures, témoignages." },
  { title: "Cérémonie religieuse", desc: "Catholique, protestante, orthodoxe, juive, musulmane : nous travaillons avec les officiants de votre confession." },
  { title: "Inhumation", desc: "En caveau familial ou pleine terre, dans le cimetière de votre choix. Nous gérons concession et formalités." },
  { title: "Crémation", desc: "Coordination avec le crématorium, choix de l'urne, dispersion ou conservation : un accompagnement complet." },
];

export default function OrganisationPage() {
  usePageMeta(
    "Organisation d'obsèques — civiles et religieuses",
    "Cinq étapes claires pour organiser des obsèques à Baho et Perpignan : du premier appel jusqu'aux démarches après cérémonie. Inhumation, crémation, cérémonies civiles et religieuses."
  );
  return (
    <div data-testid="organisation-page">
      <section className="relative bg-black text-white py-28 lg:py-40">
        <div className="absolute inset-0 opacity-30">
          <img src={IMAGES.sky} alt="" className="w-full h-full object-cover grayscale" />
        </div>
        <div className="relative container-soft">
          <p className="overline text-white/60">Organisation d'obsèques</p>
          <h1 className="font-serif-display text-4xl sm:text-6xl lg:text-7xl mt-4 max-w-4xl tracking-tight leading-[1.02]">
            Cinq étapes, <em className="italic font-light">une seule présence</em> à vos côtés.
          </h1>
          <p className="mt-8 max-w-2xl text-white/80 text-lg leading-relaxed">
            De l'instant qui suit l'annonce jusqu'aux démarches finales, nous orchestrons
            chaque détail. Vous gardez la main sur les choix qui comptent ; nous gérons le reste.
          </p>
        </div>
      </section>

      {/* STEPS */}
      <section className="py-24 lg:py-32">
        <div className="container-soft">
          <SectionTitle
            overline="Le parcours"
            title="Un déroulé clair, sans aucune zone d'ombre."
            kicker="À chaque étape, vous savez ce qui se passe, qui le fait, et combien cela coûte."
          />
          <div className="mt-16 space-y-px bg-black/10 border border-black/10">
            {STEPS.map((s) => (
              <div
                key={s.n}
                data-testid={`organisation-step-${s.n}`}
                className="bg-white p-8 lg:p-12 grid md:grid-cols-12 gap-6 items-start hover:bg-neutral-50 transition-colors duration-500"
              >
                <div className="md:col-span-2 font-serif-display text-5xl lg:text-6xl text-black/30 leading-none">{s.n}</div>
                <div className="md:col-span-10">
                  <h3 className="font-serif-display text-2xl lg:text-3xl">{s.title}</h3>
                  <p className="mt-3 text-neutral-600 leading-relaxed text-lg">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TYPES */}
      <section className="bg-neutral-50 py-24 lg:py-32 border-y border-black/10">
        <div className="container-soft">
          <SectionTitle
            overline="Cérémonies"
            title="Civile ou religieuse, inhumation ou crémation."
            kicker="Quel que soit votre choix, nous adaptons chaque détail dans le respect des volontés et des croyances."
          />
          <div className="grid md:grid-cols-2 gap-px bg-black/10 mt-16 border border-black/10">
            {TYPES.map((t, i) => (
              <div key={i} data-testid={`ceremony-type-${i}`} className="bg-white p-10">
                <h3 className="font-serif-display text-2xl">{t.title}</h3>
                <p className="mt-4 text-neutral-600 leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PriceBanner />

      <section className="py-24 lg:py-32 text-center">
        <div className="container-soft">
          <SectionTitle
            overline="Vous êtes confronté à un décès"
            title="Appelez-nous maintenant."
            kicker="Un conseiller vous répond immédiatement, à toute heure du jour ou de la nuit."
            align="center"
          />
          <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
            <a href={SITE.phoneHref} data-testid="organisation-cta-call" className="inline-flex items-center justify-center gap-3 bg-black text-white px-8 py-5 rounded-sm text-sm tracking-wide hover:bg-neutral-800 transition-all duration-300">
              <Phone size={18} strokeWidth={1.5} /> {SITE.phone}
            </a>
            <Link to="/contact" data-testid="organisation-cta-contact" className="inline-flex items-center justify-center gap-2 border border-black px-8 py-5 rounded-sm text-sm tracking-wide hover:bg-black hover:text-white transition-all duration-300">
              Écrire un message <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
